// "Discuss this storyboard" (Adam, 16 Sep 2026). A storyboard is framed from
// its own /app/ route, so its fragment (story.html) and datasets (<name>.json)
// are one fetch away from the page that frames it. This hook reads them,
// summarises the page for the agent, and registers the storyboard tools while
// the page is mounted. The discussion itself is the page helper's chat
// (`PageAgentDrawer`, pageKey "storyboard", Douglas plan T17).
import { RefObject, useEffect, useRef, useState } from 'react'
import useAccount from './useAccount'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import {
  buildStoryboardVoiceTools,
  cellFigure,
  parseStoryboard,
  storyboardDatasetNames,
  storyboardPageContext,
  StoryboardDataset,
  StoryboardSummary,
} from '../voice/storyboard'
import { goMessage, parseDeckAt, postToDeck } from '../agent/presentationFrame'

export type StoryboardDiscussionStatus = 'idle' | 'loading' | 'ready' | 'unavailable'

interface UseStoryboardDiscussionOptions {
  /** The app route the storyboard is framed from, with its trailing slash; null when the page is not a storyboard. */
  appUrl: string | null
  /** The frame showing the storyboard, for show_storyboard_section to scroll. */
  iframeRef: RefObject<HTMLIFrameElement | null>
}

export interface StoryboardDiscussion {
  status: StoryboardDiscussionStatus
  summary: StoryboardSummary | null
  /** The button may be shown: a signed-in Manager, and the page has been read. */
  canDiscuss: boolean
}

type DatasetCache = Map<string, Promise<StoryboardDataset | null>>

/** One dataset from the app route; null for a missing file. Cached per name for the life of the page. */
function fetchDataset(appUrl: string, cache: DatasetCache, name: string): Promise<StoryboardDataset | null> {
  const hit = cache.get(name)
  if (hit) return hit
  const p = fetch(`${appUrl}${encodeURIComponent(name)}.json`)
    .then((res) => (res.ok ? (res.json() as Promise<StoryboardDataset>) : null))
    .catch(() => null)
  cache.set(name, p)
  return p
}

/** The fragment, read and summarised with every figure it names resolved from the datasets. */
async function loadStoryboard(appUrl: string, cache: DatasetCache): Promise<StoryboardSummary> {
  const res = await fetch(`${appUrl}story.html`)
  if (!res.ok) throw new Error(`story.html: HTTP ${res.status}`)
  const html = await res.text()
  const loaded = new Map<string, StoryboardDataset>()
  await Promise.all(
    storyboardDatasetNames(html).map(async (name) => {
      const data = await fetchDataset(appUrl, cache, name)
      if (data) loaded.set(name, data)
    }),
  )
  return parseStoryboard(html, (from, row, col, metric, decimals) => {
    const data = loaded.get(from)
    return data ? cellFigure(data, row, col, metric, decimals) : undefined
  })
}

/** Scrolls the framed storyboard to a section (or its top). Same-origin frame only. */
function scrollStoryboardTo(iframe: HTMLIFrameElement | null, title: string): boolean {
  const doc = iframe?.contentDocument
  if (!iframe || !doc) return false
  // The frame itself first, so the person sees the page move.
  iframe.scrollIntoView({ behavior: 'smooth', block: 'start' })
  if (title === 'top') {
    iframe.contentWindow?.scrollTo({ top: 0, behavior: 'smooth' })
    return true
  }
  const want = title.trim()
  const el = Array.from(doc.querySelectorAll('pt-section')).find((s) => (s.getAttribute('title') ?? '').trim() === want)
  if (!el) return false
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return true
}

/**
 * Moves a framed presentation to a slide or layer (or its first slide) with
 * the host's `go` message: a deck shows one slide at a time, so scrolling
 * would find nothing on screen.
 */
export function goToDeckSection(iframe: HTMLIFrameElement | null, summary: StoryboardSummary, title: string): boolean {
  if (!iframe?.contentWindow) return false
  const section = title === 'top' ? summary.sections.find((s) => s.kind === 'slide') : summary.sections.find((s) => s.title.trim() === title.trim())
  const at = parseDeckAt(section?.at)
  if (!at) return false
  iframe.scrollIntoView({ behavior: 'smooth', block: 'start' })
  postToDeck(iframe.contentWindow, goMessage(at))
  return true
}

export default function useStoryboardDiscussion({ appUrl, iframeRef }: UseStoryboardDiscussionOptions): StoryboardDiscussion {
  const account = useAccount()
  const [state, setState] = useState<{ appUrl: string | null; status: StoryboardDiscussionStatus; summary: StoryboardSummary | null }>({
    appUrl: null,
    status: 'idle',
    summary: null,
  })
  const cacheRef = useRef<DatasetCache>(new Map())
  const appUrlRef = useRef(appUrl)
  appUrlRef.current = appUrl

  // When the storyboard changes, read it. The result is keyed by appUrl so a
  // stale read never describes the wrong page.
  useEffect(() => {
    if (!appUrl) return
    let cancelled = false
    const cache: DatasetCache = new Map()
    cacheRef.current = cache
    setState({ appUrl, status: 'loading', summary: null })
    loadStoryboard(appUrl, cache).then(
      (summary) => {
        if (!cancelled) setState({ appUrl, status: 'ready', summary })
      },
      () => {
        if (!cancelled) setState({ appUrl, status: 'unavailable', summary: null })
      },
    )
    return () => {
      cancelled = true
    }
  }, [appUrl])

  const current = state.appUrl === appUrl ? state : { appUrl, status: 'idle' as const, summary: null }
  const summaryRef = useRef<StoryboardSummary | null>(current.summary)
  summaryRef.current = current.summary

  const uiTools = appUrl
    ? buildStoryboardVoiceTools({
        summary: () => summaryRef.current,
        loadDataset: (name) => (appUrlRef.current ? fetchDataset(appUrlRef.current, cacheRef.current, name) : Promise.resolve(null)),
        showSection: (title) => {
          const s = summaryRef.current
          const shown = s?.isDeck ? goToDeckSection(iframeRef.current, s, title) : scrollStoryboardTo(iframeRef.current, title)
          if (!shown) return null
          return title === 'top' ? summaryRef.current?.title || 'the top of the page' : title
        },
      })
    : []

  useVoiceRegistration({
    pageKey: `storyboard:${appUrl ?? 'none'}`,
    uiTools,
    pageContext: () => {
      const s = summaryRef.current
      if (s) return storyboardPageContext(s)
      return appUrl ? 'A storyboard (a page of findings) is on screen and still loading; its summary is not available yet.' : ''
    },
  })

  const isManager = !!account.user?.token && !!account.user?.role_permissions?.isManager

  return {
    status: current.status,
    summary: current.summary,
    canDiscuss: isManager && current.status === 'ready',
  }
}
